import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Calendar, FileText, TrendingUp, Zap, ArrowRight, CheckCircle, Lock } from "lucide-react";
import { usePlanAccess } from "../components/PlanGate";

const TOOLS = [
  { icon: Zap, title: "AI Bid Document Generator", desc: "Generate professional EOIs, cover letters, and technical proposals in seconds", path: "/tenders", action: "Select a tender first", color: "var(--accent-green)", dim: "var(--accent-green-dim)", badge: "AI Powered" },
  { icon: Shield, title: "HSE Report Generator", desc: "Create Risk Assessments, Incident Reports, Safety Audits, and Method Statements", path: "/hse-report", color: "var(--accent-orange)", dim: "var(--accent-orange-dim)", badge: "AI Powered" },
  { icon: Calendar, title: "Regulatory Calendar", desc: "Track CAC, FIRS, PENCOM, NSITF, and all Nigerian compliance deadlines", path: "/regulatory-calendar", color: "var(--accent-blue)", dim: "var(--accent-blue-dim)", badge: "Live Tracker" },
  { icon: TrendingUp, title: "Market Intelligence", desc: "AI-generated market reports using live tender data from across Nigeria", path: "/market-intelligence", color: "var(--accent-green)", dim: "var(--accent-green-dim)", badge: "Live Data" },
  { icon: FileText, title: "Compliance Score Checker", desc: "Check your bid compliance readiness and get AI recommendations", path: "/tenders", action: "Select a tender first", color: "var(--accent-blue)", dim: "var(--accent-blue-dim)", badge: "Per Tender" },
];

export default function ComplianceHub() {
  const navigate = useNavigate();
  const hasAccess = usePlanAccess("compliance_hub");

  if (!hasAccess) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: '72px', height: '72px', borderRadius: '20px', background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
        <Lock size={28} color="var(--accent-orange)" />
      </div>
      <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--accent-orange)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>Enterprise Plan Required</div>
      <h2 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '12px' }}>Compliance Hub is locked</h2>
      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', maxWidth: '380px', lineHeight: '1.7', marginBottom: '32px' }}>
        Upgrade to the <strong style={{ color: 'var(--accent-orange)' }}>Enterprise plan</strong> (₦75k/month) to unlock HSE reports, regulatory calendar, compliance scoring, and market intelligence.
      </p>
      <button onClick={() => navigate('/billing')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 28px', background: 'linear-gradient(135deg, var(--accent-orange), #e05a20)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
        <Zap size={15} /> Upgrade to Enterprise →
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontSize: "26px", marginBottom: "6px" }}>Compliance & Reporting Hub</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>All your compliance tools, reports, and document generators in one place</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "28px" }}>
        {TOOLS.map(({ icon: Icon, title, desc, path, color, dim, badge, action }) => (
          <div key={title} className="card" style={{ cursor: "pointer", borderLeft: `3px solid ${color}`, transition: "all 0.2s" }}
            onClick={() => navigate(path)}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
              <div style={{ width: "44px", height: "44px", background: dim, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={22} color={color} />
              </div>
              <span style={{ fontSize: "11px", background: dim, color, padding: "3px 10px", borderRadius: "100px", fontWeight: "600" }}>{badge}</span>
            </div>
            <h3 style={{ fontSize: "16px", marginBottom: "6px" }}>{title}</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6", marginBottom: "14px" }}>{desc}</p>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", color, fontSize: "13px", fontWeight: "600" }}>
              {action || "Open Tool"} <ArrowRight size={14} />
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ background: "linear-gradient(135deg, rgba(0,212,161,0.08), rgba(79,142,247,0.05))", borderColor: "var(--border-active)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <CheckCircle size={20} color="var(--accent-green)" />
          <h3 style={{ fontSize: "16px" }}>Nigerian Compliance Checklist</h3>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
          {[
            { label: "CAC Registration", req: "Required" },
            { label: "TIN (FIRS)", req: "Required" },
            { label: "PENCOM Cert", req: "Required" },
            { label: "NSITF Cert", req: "Required" },
            { label: "ITF Cert", req: "Required" },
            { label: "BPP Portal Reg", req: "For FG Tenders" },
            { label: "ISO 9001", req: "For Large Tenders" },
            { label: "Audited Accounts", req: "3 Years" },
          ].map(({ label, req }) => (
            <div key={label} style={{ padding: "10px 12px", background: "var(--bg-secondary)", borderRadius: "8px", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: "12px", fontWeight: "600", marginBottom: "2px" }}>{label}</div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{req}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
